import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { getCheckoutStatus } from "@/lib/api";
import { analytics } from "@/lib/analytics";
import BrandBand from "@/components/BrandBand";

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking");
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let attempts = 0;
    const maxAttempts = 6;
    let fired = false;
    const poll = async () => {
      try {
        const res = await getCheckoutStatus(sessionId);
        setData(res);
        if (res.payment_status === "paid") {
          if (!fired) { analytics.purchaseCompleted(res.resource_slug, res.amount || 0); fired = true; }
          setStatus("paid");
          return;
        }
        if (res.status === "expired") { setStatus("expired"); return; }
        attempts++;
        if (attempts >= maxAttempts) { setStatus("timeout"); return; }
        setTimeout(poll, 2000);
      } catch {
        setStatus("error");
      }
    };
    poll();
  }, [sessionId]);

  return (
    <div data-testid="checkout-success-page">
      <section className="section">
        <div className="max-w-[900px] mx-auto px-6 md:px-10">
          {status === "checking" && (
            <div data-testid="checkout-checking">
              <div className="overline">Verifying payment</div>
              <h1 className="font-serif-editorial tight-track mt-6" style={{ fontSize: "clamp(2.4rem, 5vw, 4rem)", lineHeight: 1 }}>One moment.</h1>
              <p className="mt-6 text-lg opacity-80">Confirming your purchase with Stripe…</p>
            </div>
          )}
          {status === "paid" && (
            <div data-testid="checkout-paid">
              <div className="overline">Thank you</div>
              <h1 className="font-serif-editorial tight-track mt-6" style={{ fontSize: "clamp(2.4rem, 5vw, 4rem)", lineHeight: 1 }}>
                Your download is on its way.
              </h1>
              <p className="mt-8 text-xl font-serif-editorial" style={{ maxWidth: "36rem" }}>
                {data?.resource_title}
              </p>
              <p className="mt-6 text-lg" style={{ maxWidth: "40rem", lineHeight: 1.6 }}>
                We&apos;ve emailed your download link{data?.email ? <> to <strong>{data.email}</strong></> : null}. It usually arrives in under a minute — check spam if it doesn&apos;t appear.
              </p>
              <div className="mt-10 flex flex-wrap gap-4">
                {data?.download_url && (
                  <a href={data.download_url} className="btn-olive" data-testid="download-link">Download now</a>
                )}
                <Link to="/resources" className="btn-outline" data-testid="back-to-library">Back to library</Link>
              </div>
              <p className="mt-10 text-sm opacity-70">Your receipt is in the same email. Keep it for your records — the download link is valid for 7 days.</p>
            </div>
          )}
          {(status === "error" || status === "expired" || status === "timeout") && (
            <div data-testid="checkout-error">
              <div className="overline">Status</div>
              <h1 className="font-serif-editorial tight-track mt-6" style={{ fontSize: "clamp(2.4rem, 5vw, 4rem)", lineHeight: 1 }}>
                {status === "expired" ? "The session expired." : "We couldn't confirm your payment."}
              </h1>
              <p className="mt-8 text-lg opacity-80">Please try again, or contact us if the issue persists.</p>
              <div className="mt-10">
                <Link to="/resources" className="btn-olive">Return to resources</Link>
              </div>
            </div>
          )}
        </div>
      </section>
      <BrandBand />
    </div>
  );
}
